import { useEffect, useMemo } from 'react';
import { fetchAllWeeklyHoursForProject, fetchProject } from '../../services/projects-services';
import { fetchTemplates } from '../../services/templates-services';
import { useQuery } from '@tanstack/react-query';
import { calculateMilestones, calculateTotalBudget } from './planner.utils';
import { isSomething } from '../../utils/utils';

// Here, we alias the incoming "milestones" parameter to "deliverables" for clarity.
export const usePlannerValues = ({ milestones: deliverables = [], materials = [], project }) => {
  const projectDetails = useMemo(() => project, [project]);

  const materialTotal = useMemo(() => {
    if (!Array.isArray(materials)) return 0;
    return calculateTotalBudget(materials);
  }, [materials]);

  // If the passed deliverables array is empty, fall back to projectDetails.deliverables (or an empty array)
  const deliverableArray =
    deliverables.length > 0 ? deliverables : projectDetails?.deliverables || [];

  const noMilestoneProject = useMemo(
    () =>
      deliverableArray.length === 1 &&
      !isSomething(deliverableArray[0]?.name) &&
      !isSomething(deliverableArray[0]?.tasks?.[0]?.description),
    [deliverableArray],
  );

  return {
    projectDetails,
    materialTotal,
    noMilestoneProject,
  };
};

export const useProjectDetails = ({ projectDetails, setPlannerState, projectLoadRef }) => {
  useEffect(() => {
    if (!projectDetails || projectLoadRef?.current) return;

    const {
      serviceDiscount = 0,
      materialDiscount = 0,
      serviceDiscountUnit,
      materialDiscountUnit,
      // ← hydrate these from Firestore
      plannerStartDate = '',
      plannerEndDate = '',
      plannerServiceValue = 0,
      plannerServiceHours = 0,
      projectTotal = 0,
      ...restDetails
    } = projectDetails;

    const deliverables = projectDetails.deliverables || [];
    const materials = projectDetails.materials || [];
    const { updatedMilestones, materials: updatedMaterials } = calculateMilestones({
      milestones: deliverables,
      materials,
    });

    setPlannerState((prev) => {
      const milestonesChanged =
        JSON.stringify(prev.milestones) !== JSON.stringify(updatedMilestones);
      const materialsChanged = JSON.stringify(prev.materials) !== JSON.stringify(updatedMaterials);
      if (!milestonesChanged && !materialsChanged) return prev;

      return {
        ...prev,
        milestones: updatedMilestones,
        materials: updatedMaterials,
        serviceDiscount,
        materialDiscount,
        serviceDiscountUnit: serviceDiscountUnit || prev.serviceDiscountUnit,
        materialDiscountUnit: materialDiscountUnit || prev.materialDiscountUnit,
        projectData: {
          ...prev.projectData,
          // your existing fields
          flowId: restDetails.id || '',
          acceloId: restDetails.acceloId || '',
          acceloTitle: restDetails.acceloTitle || '',
          client: restDetails.client || '',
          projectName: restDetails.projectName || '',
          projectScope: restDetails.projectScope || '',
          plannerType: restDetails.plannerType || '',
          status: restDetails.status || '',
          start: restDetails.start || new Date().toISOString(),
          // ← plus these Fixed-Fee overrides
          plannerStartDate,
          plannerEndDate,
          plannerServiceValue,
          plannerServiceHours,
          projectTotal,
        },
      };
    });

    projectLoadRef.current = true;
  }, [projectDetails, setPlannerState]);
};

const useProjectData = (id) => {
  const { data: project, isLoading: isProjectLoading } = useQuery({
    queryKey: ['project', id],
    queryFn: () => fetchProject(id),
  });
  const { data: loggedInstances, isLoading: isLoggedInstancesLoading } = useQuery({
    queryKey: ['loggedInstances'],
    queryFn: () => fetchAllWeeklyHoursForProject(id),
  });

  const { data: templatesList, isLoading: isTemplatesLoading } = useQuery({
    queryKey: ['templates'],
    queryFn: fetchTemplates,
  });

  return {
    project,
    isProjectLoading,
    templatesList,
    isTemplatesLoading,
    loggedInstances,
    isLoggedInstancesLoading,
  };
};

export default useProjectData;
